<template>
    <div class="mead-app-sidebar-item">
        <div class="f fa-c">
            <button type="button"
                class="mead-app-sidebar-item-main f fa-c fg c-p br-m sp-s"
                :class="[
                    item.active ? 'el--active' : '',
                    item.upsell ? 'el--upsell' : '',
                    primary ? 'el--primary' : '']"
                :qa="uiQa"
                :disabled="item.disabled"
                :aria-current="item.active ? 'page' : null"
                :aria-expanded="uiHasChildren ? String(!collapsed) : null"
                @click="onClickItem">
                <mead-icon v-if="showIcon && item.icon"
                    :name="item.getIcon(item.active)"
                    uiSize="l"
                    class="sm-r-s" />
                <span class="mead-app-sidebar-item-label fg">{{ uiLabel }}</span>
                <mead-badge v-if="uiHasBadge" :lbl="item.badge" class="sm-l-s" />
            </button>
            <mead-collapse-trigger v-if="uiHasChildren"
                v-model="collapsed"
                class="mead-app-sidebar-item-trigger sm-l-xs" />
        </div>
        <div v-if="uiHasChildren && !collapsed"
            class="mead-app-sidebar-item-children b-l sm-l-m sp-l-s sm-t-xs">
            <mead-app-sidebar-item v-for="child in item.children"
                :key="child.uid"
                :item="child"
                @select="$emit('select', $event)"
                @upsell="$emit('upsell', $event)" />
        </div>
    </div>
</template>

<script>
export default {
    name: 'mead-app-sidebar-item',
    props: {
        /**
         * The `MeadCommon.AppSidebarItem` to render. Its children render below it as a
         * collapsible group.
         */
        item: {type: Object, required: true},
        /**
         * Renders the item's icon before its label.
         */
        showIcon: {type: Boolean, default: false},
        /**
         * When false, children are never rendered and clicking an item with children emits `select`
         * instead of toggling the group. The mobile menu uses this for its top level list.
         */
        expandable: {type: Boolean, default: true},
        /**
         * Styles the item for the primary (brand colored) navigation background.
         */
        primary: {type: Boolean, default: false},
    },
    data: function () {
        return {
            collapsed: false,
        };
    },
    methods: {
        onClickItem: function () {
            if (this.item.disabled) return;

            if (this.item.upsell) {
                this.$emit('upsell', this.item);
            } else if (this.uiHasChildren) {
                this.collapsed = !this.collapsed;
            } else {
                this.$emit('select', this.item);
            }
        },
    },
    computed: {
        uiHasChildren: function () {
            return this.expandable && !this.item.upsell && this.item.hasChildren;
        },
        uiHasBadge: function () {
            return this.item.hasBadge;
        },
        uiLabel: function () {
            return MeadCommon.I18N.go(this.item.label);
        },
        uiQa: function () {
            return this.item.hasQATag ? this.item.qa_tag : `app-sidebar-item-${this.item.uid}`;
        },
    },
};
</script>

<style lang="scss" scoped>
@use '../../../../sass/variables' as *;

.mead-app-sidebar-item-main {
    min-height: 4rem;
    margin: 0 0 .5rem 0;
    border: none;
    background-color: transparent;
    color: var(--c-sidebar-panel-fg);
    font-family: $ff-h;
    font-size: $fs-s;
    text-align: left;
    transition: background-color .2s ease;

    .mead-app-sidebar-item-label {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        line-height: 2rem;
    }

    &:hover,
    &.el--active {
        color: var(--c-sidebar-panel-item-fg-active);
        background-color: var(--c-sidebar-panel-item-bg-active);
    }

    &:focus-visible {
        outline: 2px solid var(--c-sidebar-rail-bg-active);
        outline-offset: 1px;
    }

    &.el--upsell,
    &:disabled {
        opacity: .5;
        cursor: not-allowed;
    }

    &:disabled:hover {
        background-color: transparent;
        color: var(--c-sidebar-panel-fg);
    }

    //  Items sitting on the primary navigation background
    &.el--primary {
        color: var(--c-sidebar-rail-fg);
        font-size: $fs-m;

        &:hover {
            background-color: var(--c-sidebar-rail-bg-hover);
            color: var(--c-sidebar-rail-fg);
        }

        &.el--active {
            background-color: var(--c-sidebar-rail-bg-active);
            color: var(--c-sidebar-rail-fg-active);
        }

        &:focus-visible {
            outline-color: var(--c-sidebar-rail-fg);
        }
    }

    @media (prefers-reduced-motion: reduce) {
        transition: none;
    }
}
</style>
